<template>
  <page-wrapper error-key="tariffSchedules2" sticky-second-row :transparent-bg="isGridView" class="remote-tariffs">
    <template #title>
      <div class="d-flex justify-space-between align-center w-100">
        <div class="h3">Подключения</div>

        <view-mode-toggle v-if="tariffsRemote?.length && $vuetify.display.mdAndUp" v-model="viewMode" />
      </div>
    </template>

    <template v-if="tariffsRemote?.length" #secondRow>
      <div class="d-flex align-center">
        <e-input v-model="filters.searchString" label="Поиск" class="remote-tariffs__search-input">
          <template #append-inner>
            <e-icon name="search" size="20" />
          </template>
        </e-input>
        <v-spacer />

        <operation-button
          class="create-tariff-button"
          prepend-icon-src="plus"
          :to="{ name: RouteNames.CMCreate }"
          :icon="$vuetify.display.smAndDown"
          :operation="operations?.canAddRemoteTariffSchedule"
          variant="primary"
        >
          <span v-if="!$vuetify.display.smAndDown">Создать подключение</span>
        </operation-button>
      </div>
    </template>

    <template v-if="tariffsRemote?.length || isLoading">
      <e-data-items
        v-if="viewMode === ViewMode.Table && $vuetify.display.mdAndUp"
        class="mt-2"
        :headers="tableHeaders"
        :items="filteredTariffs"
        :loading="isLoading"
        :row-props="rowProps"
        interactive
        sticky-header-top="76px"
        @on-row-click="handleRowClick"
      >
        <template v-slot:[`item.channelManagerCode`]="{ item }">
          <div class="d-flex align-center">
            <template v-if="item.channelManagerCode === ChannelManagerCode.TravelLine">
              <v-img :src="TLIcon" width="22" />
              <span class="ml-2">TravelLine</span>
            </template>
            <template v-else-if="item.channelManagerCode === ChannelManagerCode.Exely">
              <v-img :src="ExelyIcon" width="22" />
              <span class="ml-2">Exely</span>
            </template>
            <template v-else-if="item.channelManagerCode === ChannelManagerCode.Bnovo">
              <v-img :src="BnovoIcon" width="22" />
              <span class="ml-2">Bnovo</span>
            </template>
            <template v-else-if="item.channelManagerCode === ChannelManagerCode.Kontur">
              <v-img :src="KonturIcon" width="22" />
              <span class="ml-2">Контур Отель</span>
            </template>
          </div>
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <status-string v-bind="getTariffStatus(item)" />
        </template>
      </e-data-items>

      <div v-else class="tariffs-grid">
        <template v-if="isLoading">
          <skeleton-loader v-for="item in 10" height="200px" :key="item" />
        </template>
        <template v-else>
          <div
            v-for="(item, index) in filteredTariffs"
            :key="index"
            @click="handleRowClick($event, { item })"
            class="tariffs-card"
          >
            <div class="d-flex justify-space-between mb-3">
              <status-string v-bind="getTariffStatus(item)" />
            </div>
            <div class="paragraph-l font-600 mb-4">{{ item.name[0].value }}</div>
            <div class="d-flex align-center justify-end mt-auto">
              <template v-if="item.channelManagerCode === ChannelManagerCode.TravelLine">
                <v-img :src="TLIcon" width="22" />
                <span class="paragraph-m font-600 px-2">TravelLine</span>
              </template>
              <template v-else-if="item.channelManagerCode === ChannelManagerCode.Exely">
                <v-img :src="ExelyIcon" width="22" />
                <span class="paragraph-m font-600 px-2">Exely</span>
              </template>
              <template v-else-if="item.channelManagerCode === ChannelManagerCode.Bnovo">
                <v-img :src="BnovoIcon" width="22" />
                <span class="paragraph-m font-600 px-2">Bnovo</span>
              </template>
              <template v-else-if="item.channelManagerCode === ChannelManagerCode.Kontur">
                <v-img :src="KonturIcon" width="22" />
                <span class="paragraph-m font-600 px-2">Контур Отель</span>
              </template>
            </div>
          </div>
        </template>
      </div>
    </template>

    <template v-else>
      <page-content-placeholder
        v-if="noAccounts"
        text="Прежде чем добавить тарифный план вам необходимо создать аккаунт"
        :img-src="PlaceholderImage"
      >
        <create-account-dialog>
          <template #activator>
            <e-btn variant="primary" class="mt-6" prepend-icon-src="plus"> Создать аккаунт</e-btn>
          </template>
        </create-account-dialog>
      </page-content-placeholder>

      <page-content-placeholder v-else text="У вас нет подключенных менеджеров каналов" :img-src="PlaceholderImage">
        <operation-button
          variant="primary"
          class="mt-6"
          prepend-icon-src="plus"
          :operation="operations?.canAddRemoteTariffSchedule"
          :to="{ name: RouteNames.CMCreate }"
        >
          Создать подключение
        </operation-button>
      </page-content-placeholder>
    </template>
  </page-wrapper>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDisplay } from 'vuetify';
import type { RemoteTariffType } from '@/parts/tariffs/store/tariffs';
import BnovoIcon from '@/assets/img/pages/tariffs/channel-manager/bnovo.svg';
import ExelyIcon from '@/assets/img/pages/tariffs/channel-manager/exely.svg';
import KonturIcon from '@/assets/img/pages/tariffs/channel-manager/kontur.svg';
import PlaceholderImage from '@/assets/img/pages/tariffs/channel-manager/placeholder_image.svg';
import TLIcon from '@/assets/img/pages/tariffs/channel-manager/travel_line.svg';
import OperationButton from '@/core/components/buttons/OperationButton.vue';
import ContextMenu from '@/core/components/common/ContextMenu.vue';
import PageContentPlaceholder from '@/core/components/common/PageContentPlaceholder.vue';
import StatusString from '@/core/components/common/StatusString.vue';
import ViewModeToggle from '@/core/components/common/ViewModeToggle.vue';
import EDataItems from '@/core/components/common/data-items/EDataItems.vue';
import PageWrapper from '@/core/components/interface/PageWrapper.vue';
import SkeletonLoader from '@/core/components/interface/SkeletonLoader.vue';
import { EBtn, EIcon, EInput } from '@/core/components/ui';
import { useFetch } from '@/core/composables/useFetch';
import { useUserPreference } from '@/core/composables/useUserPreferences';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { ViewMode } from '@/core/types/components/helps';
import { StatusVariantColor } from '@/core/types/components/status';
import { containsSubstring } from '@/core/utils/string';
import { ChannelManagerCode, ContractStatus } from '@/graphql/generated';
import CreateAccountDialog from '@/parts/objects/components/CreateAccountDialog.vue';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';
import { useTariffsStore } from '@/parts/tariffs/store/tariffs';

const $route = useRoute();
const $router = useRouter();
const display = useDisplay();
const { accommodationObject, activeAccount } = useAccommodationObjectsStore();
const { tariffsRemote, getActiveTariffs, operations } = useTariffsStore();

const viewMode = useUserPreference('tariffsRemotePageViewMode', ViewMode.Table);

const filters = reactive({
  searchString: '',
});

const isGridView = computed(() => viewMode.value === ViewMode.Grid || display.smAndDown.value);

const noAccounts = computed(() => !accommodationObject.value?.contracts?.length);

const loadTariffs = async () => {
  await getActiveTariffs();
};

const tableHeaders = [
  {
    key: 'channelManagerCode',
    title: 'Менеджер Каналов',
  },
  {
    key: 'name[0].value',
    title: 'Наименование Подключения',
  },
  {
    key: 'status',
    title: 'Статус',
    sortRaw: (v1: RemoteTariffType, v2: RemoteTariffType) =>
      `${getTariffStatus(v2).text}` > `${getTariffStatus(v1).text}` ? -1 : 1,
  },
];

const filteredTariffs = computed(() => {
  return (
    tariffsRemote.value?.filter((item) => {
      return !filters.searchString || containsSubstring(item.name[0].value, filters.searchString);
    }) || []
  );
});

const getTariffStatus = (
  tariff: RemoteTariffType,
): {
  text: string;
  variant: StatusVariantColor;
} => {
  if (tariff.tariffSchedule) {
    if (tariff.tariffSchedule.contractClosed) {
      return { text: 'Закрыто', variant: StatusVariantColor.Red };
    }

    if (tariff.tariffSchedule.activeRemoting) {
      return { text: 'Активно', variant: StatusVariantColor.Green };
    }

    return { text: 'Не активно', variant: StatusVariantColor.Ordinary };
  }

  return { text: 'Настройка не завершена', variant: StatusVariantColor.Yellow };
};

const rowProps = ({ item }: { item: RemoteTariffType }) => {
  return {
    class: !item.tariffSchedule && activeAccount.value?.status === ContractStatus.Closed ? 'inactive' : '',
  };
};

const toEdit = (id: string) => {
  $router.push({
    name: RouteNames.TariffInfo,
    params: { id: $route.params.id, t_id: id },
  });
};

const toCreate = (interactionId: string, channelManagerCode: ChannelManagerCode) => {
  $router.push({
    name: RouteNames.CMCreate,
    query: { interactionId, manager: channelManagerCode },
  });
};

const handleRowClick = (e: Event, row: { item: RemoteTariffType }) => {
  if (row.item.tariffSchedule) {
    toEdit(row.item.tariffSchedule.tariffScheduleCid);
  } else if (activeAccount.value?.status !== ContractStatus.Closed) {
    toCreate(row.item.interactionId, row.item.channelManagerCode);
  }
};

const { isLoading } = useFetch(async () => {
  await loadTariffs();
});
</script>

<style scoped lang="scss">
.remote-tariffs {
  @include sm-and-down {
    padding-bottom: 80px;
  }

  &__search-input {
    @include md-and-up {
      max-width: 280px;
    }
  }
}

.tariffs-grid {
  display: grid;

  grid-gap: 16px;
  grid-template-columns: repeat(3, minmax(0, 1fr));

  @include md-and-down {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @include sm-and-down {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
}

.tariffs-card {
  display: flex;
  flex-direction: column;

  min-height: 200px;
  padding: 16px;

  background-color: $main-bg-color;
  border-radius: $border-radius-l;

  cursor: pointer;

  @include hover {
    box-shadow: $border-box-shadow;
  }
}

.create-tariff-button {
  @include sm-and-down {
    position: fixed;
    right: 16px;
    bottom: 16px;
    z-index: $z-index-3;
  }
}
</style>
